import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Divider,
  Alert,
} from "@mui/material";
import {
  Settings as SettingsIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  SystemUpdate as SystemUpdateIcon,
  Notifications as NotificationIcon,
  Build as BuildIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import Layout from "../common/Layout";
import { useAuth } from "../../context/AuthContext";
import systemAPI from "../../utils/systemAPI";

const DeveloperDashboard = () => {
  const { userProfile, loading: authLoading } = useAuth();
  const [systemStats, setSystemStats] = useState({
    departments: 0,
    administrators: 0,
    users: 0,
    loading: true,
    error: null,
  });
  const [activities, setActivities] = useState({
    data: [],
    loading: true,
    error: null,
  });

  // Fetch system statistics on component mount
  useEffect(() => {
    const fetchSystemStats = async () => {
      try {
        const statusResponse = await systemAPI.getSystemStatus();
        setSystemStats({
          departments: statusResponse.data.departmentCount || 0,
          administrators: statusResponse.data.adminCount || 0,
          users: statusResponse.data.userCount || 0,
          loading: false,
          error: null,
        });
      } catch (err) {
        console.error("Error fetching system stats:", err);
        setSystemStats((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to load system statistics",
        }));
      }
    };
    fetchSystemStats();
  }, []);

  // Fetch recent system activities
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const response = await systemAPI.getRecentActivities();
        setActivities({
          data: response.data || [],
          loading: false,
          error: null,
        });
      } catch (err) {
        console.error("Error fetching recent activities:", err);
        setActivities({
          data: [],
          loading: false,
          error: "Failed to load recent activities",
        });
      }
    };
    fetchActivities();
  }, []);

  // Helper function to get the appropriate icon for an activity type
  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case "DEPARTMENT_CREATED":
      case "DEPARTMENT_UPDATED":
      case "DEPARTMENT_DELETED":
        return <BusinessIcon color="info" />;
      case "ADMIN_CREATED":
      case "ADMIN_UPDATED":
      case "USER_ROLE_CHANGED":
        return <PeopleIcon color="success" />;
      case "SYSTEM_CONFIG_UPDATED":
      case "SYSTEM_SETTINGS_UPDATED":
        return <SystemUpdateIcon color="warning" />;
      case "ERROR":
        return <ErrorIcon color="error" />;
      case "MAINTENANCE":
        return <BuildIcon color="secondary" />;
      default:
        return <NotificationIcon color="primary" />;
    }
  };

  if (authLoading) {
    return (
      <Layout title="Loading...">
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h5">Loading user data...</Typography>
        </Box>
      </Layout>
    );
  }

  if (!userProfile || userProfile.role !== "developer-master") {
    return (
      <Layout title="Access Restricted">
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h4" color="error" gutterBottom>
            Access Restricted
          </Typography>
          <Typography variant="body1">
            You do not have permission to access the Developer Master dashboard.
            This area is restricted to users with the Developer Master role.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Current role: {userProfile?.role || "No role assigned"}
          </Typography>
        </Box>
      </Layout>
    );
  }

  // Only render the dashboard overview (no tabs/sidebar)
  return (
    <Layout title="Developer Master Dashboard">
      {/* System Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <BusinessIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Departments</Typography>
              </Box>
              <Typography variant="h3" color="text.primary">
                {systemStats.loading ? "..." : systemStats.departments}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {systemStats.departments === 1
                  ? "1 department is"
                  : `${systemStats.departments} departments are`}{" "}
                currently configured
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PeopleIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Administrators</Typography>
              </Box>
              <Typography variant="h3" color="text.primary">
                {systemStats.loading ? "..." : systemStats.administrators}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {systemStats.administrators === 1
                  ? "1 department administrator"
                  : `${systemStats.administrators} department administrators`}{" "}
                configured
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <SettingsIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">System Settings</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Configure global system settings, default values, and predefined
                data
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Recent System Activity */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Recent System Activity
        </Typography>
        {activities.loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={30} />
          </Box>
        ) : activities.error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {activities.error}
          </Alert>
        ) : activities.data.length === 0 ? (
          <Alert severity="info">No recent activities found.</Alert>
        ) : (
          <List>
            {activities.data.map((activity, index) => (
              <React.Fragment key={activity.id || index}>
                <ListItem>
                  <ListItemIcon>{getActivityIcon(activity.type)}</ListItemIcon>
                  <ListItemText
                    primary={activity.title || "System Event"}
                    secondary={
                      <>
                        <Typography component="span" variant="body2">
                          {activity.message || "No details available"}
                        </Typography>
                        <Typography
                          component="span"
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {activity.timestamp
                            ? new Date(activity.timestamp).toLocaleString()
                            : "No timestamp"}{" "}
                          •
                          {activity.performedBy
                            ? ` By ${activity.performedBy}`
                            : ""}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                {index < activities.data.length - 1 && (
                  <Divider component="li" />
                )}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Layout>
  );
};

export default DeveloperDashboard;
